import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

// ============================================================================
// useDispatchSearch — header-dropdown dispatch lookup.
//
// v1 (BUILD-23): matches on dispatch NUMBER substring only. Common pattern
// is a dispatcher reading a number off a ticket / customer email / Podium
// chat and wanting to land on that dispatch directly. Tech-name and customer-
// name searches stay in their respective categories (Techs deferred, Customers
// via useCustomerSearch). Keeping the dispatch query narrow means we don't
// duplicate customer-name matches across two sections.
//
// Returns top 6 most-recent matching dispatches, enriched with the customer's
// fullname via a small batched mirror_customer lookup so the dropdown row
// can show "Customer Name · 5115 Arden Drive" instead of just an address.
// ============================================================================

export interface DispatchSearchResult {
  dispatch: string;
  appointment_date: string;
  tpromtime: string | null;
  window_code: string | null;
  status: string | null;
  tech_name: string | null;
  tech_empno: string | null;
  custno: string;
  customer_fullname: string | null;
  locname: string | null;
  service_address: string | null;
  city: string | null;
}

export function useDispatchSearch(query: string) {
  return useQuery({
    queryKey: ["dispatch_search", query],
    queryFn: async (): Promise<DispatchSearchResult[]> => {
      const q = query.trim();
      // Dispatch numbers in ESC are at least 3 digits; below that the
      // substring match is too noisy to be useful.
      if (q.length < 3) return [];

      const pattern = `%${q.replace(/[%_]/g, (m) => "\\" + m)}%`;

      const { data, error } = await supabase
        .from("v_route_data")
        .select(
          "dispatch, appointment_date, tpromtime, window_code, status, " +
            "tech_name, tech_empno, custno, locname, service_address, city",
        )
        .ilike("dispatch", pattern)
        .order("appointment_date", { ascending: false })
        .limit(6);
      if (error) throw error;
      const dispatches = (data ?? []) as unknown as Omit<DispatchSearchResult, "customer_fullname">[];
      if (dispatches.length === 0) return [];

      // Enrich with customer fullnames via batched lookup.
      const custnos = Array.from(
        new Set(
          dispatches
            .map((d) => d.custno)
            .filter((c): c is string => !!c && c.trim().length > 0),
        ),
      );

      const nameByCustno = new Map<string, string | null>();
      if (custnos.length > 0) {
        const { data: customers } = await supabase
          .from("mirror_customer")
          .select("custno, fullname")
          .in("custno", custnos);
        for (const c of (customers ?? []) as {
          custno: string;
          fullname: string | null;
        }[]) {
          nameByCustno.set(c.custno, c.fullname);
        }
      }

      return dispatches.map((d) => ({
        ...d,
        customer_fullname: nameByCustno.get(d.custno) ?? null,
      })) as DispatchSearchResult[];
    },
    enabled: query.trim().length >= 3,
    staleTime: 30 * 1000,
  });
}
